import { useEffect } from "react";
import { Box, Stack, Typography } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { colors } from "brand";
import { TableSkeleton } from "common-fe";
import { useTranslation } from "i18n-fe";
import { useClassResults, RESULTS_POLL_MS } from "../hooks/useClassResults";
import {
  buildClassResults,
  isClassResultsFinal,
  isClassResultsRevealed,
  RESULT_STATUS,
} from "../utils/orderOfGo";
import { formatComposite } from "../utils/formatComposite";

// #644 — the class-level results view (docs/score/announcer.md, Screens #4 /
// Decision #2): "possibly the announcer reads placings, but the information
// must be available either way." Sits directly under each ClassHeader so the
// full placing-ordered list for that class is somewhere the announcer can just
// look, no extra tap, once every ride in the class is FINAL.
//
// Only the CURRENT/live class polls (RESULTS_POLL_MS, ~15s) — every other
// class fetches once, since a FINAL class's results don't change again. That
// matches docs/score/announcer.md's freshness table exactly.
//
// ⚠️ Same dark-mode trap as RideRow: brand `primary.main` is dark grey and
// disappears on a dark background, so emphasis here is colors.foxOrange /
// text.* only.
export default function ClassResults({ classId, isLive, rideRows, onResults, online = true }) {
  const { t } = useTranslation("announcer");
  const { results } = useClassResults(classId, {
    intervalMs: isLive ? RESULTS_POLL_MS : undefined,
    online, // #705 — offline falls back to last-cached placings/composites
  });

  // Lift the raw results up to OrderOfGo so it can badge each RideRow's
  // AccordionSummary with its placing — the class-level list below is not the
  // only place a placing shows up (docs/score/announcer.md: "a single ride's
  // badge alone doesn't show ORDER", so both views exist together).
  useEffect(() => {
    onResults?.(classId, results);
  }, [classId, results, onResults]);

  // #714 — still loading the first response: a small skeleton instead of a
  // blank flash (never a stale/empty state, per the original comment).
  if (results === null) {
    return (
      <Box
        data-testid="class-results-loading"
        data-class-id={classId}
        sx={{ mt: 1, mb: 1, p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider" }}
      >
        <TableSkeleton rows={2} dense showHeader={false} />
      </Box>
    );
  }

  const rows = buildClassResults(results, rideRows);
  const final = isClassResultsFinal(results);
  // #699 — scores are readable once every ride is CONFIRMED (all judges
  // confirmed their marks), not only once every ride is signed FINAL. Rides
  // that are CONFIRMED-but-not-FINAL show their composite in green ("ready to
  // announce") with no placing yet; FINAL rows keep the orange placing.
  const revealed = isClassResultsRevealed(results);

  return (
    <Box
      data-testid="class-results"
      data-class-id={classId}
      data-final={final || undefined}
      data-revealed={revealed || undefined}
      sx={{
        mt: 1,
        mb: 1,
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: revealed ? 1.5 : 0 }}>
        <EmojiEventsIcon sx={{ color: colors.foxOrange }} />
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1, color: "text.secondary" }}>
          {t("results.heading")}
        </Typography>
      </Stack>

      {!revealed ? (
        <Typography data-testid="class-results-not-final" color="text.secondary">
          {t("results.notFinal")}
        </Typography>
      ) : (
        <Stack spacing={1} data-testid="class-results-list">
          {rows.map((row) => {
            const rowConfirmed = row.status === RESULT_STATUS.CONFIRMED;
            return (
              <Stack
                key={row.rideId}
                direction="row"
                spacing={2}
                alignItems="center"
                data-testid="class-results-row"
                data-ride-id={row.rideId}
                data-placing={row.placing ?? undefined}
                data-status={row.status || undefined}
              >
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{ minWidth: 48, color: row.placing ? colors.foxOrange : "text.secondary" }}
                >
                  {row.placing || t("results.unplaced")}
                </Typography>
                <Typography sx={{ minWidth: 56 }} color="text.secondary">
                  {row.competitorNumber || "—"}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <Typography variant="body1" fontWeight={700}>
                    {row.riderName || "—"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {row.horseName || "—"}
                  </Typography>
                </Box>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  data-testid="class-results-composite"
                  sx={{ color: rowConfirmed ? "success.main" : "text.primary" }}
                >
                  {t("results.composite", { value: formatComposite(row.composite) })}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
